import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { appTaskScreenConfigState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppDao } from '@rock/db-admin';
import type { AppTaskScreenConfig, EquipmentTypeId } from '@rock/types';
import { INITIAL_TASK_SCREEN_CONFIG, TASK_SECTION_LABELS } from '@rock/tasks';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowDown, ArrowUp, CircleX, PlusCircle } from 'lucide-react';
import { type FC, useCallback } from 'react';
import { type UseFormReturn, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

const log = logger('DefaultTaskScreenPage');
const appDao = new AppDao();

const FormSectionSchema = z.object({
    type: z.enum(['instructions', 'history', 'pictures', 'notes', 'supplies', 'contact']),
    enabled: z.boolean(),
    defaultEnabled: z.boolean(),
    title: z.string().optional(),
    description: z.string().optional(),
    slots: z
        .array(
            z.object({
                category: z.enum(['before', 'after', 'other']),
                title: z.string().min(1, 'Label is required'),
            }),
        )
        .optional(),
    suppliesConfig: z
        .array(
            z.object({
                equipmentTypeId: z.string(),
                quantityPerTask: z.number(),
            }),
        )
        .optional(),
});

const FormSchema = z.object({
    sections: z.array(FormSectionSchema),
});

type FormValues = z.infer<typeof FormSchema>;

function toFormValues(config: AppTaskScreenConfig | undefined): FormValues {
    if (config) {
        return {
            sections: config.sections.map((s) => ({
                type: s.type,
                enabled: s.enabled,
                defaultEnabled: s.defaultEnabled,
                title: s.title,
                description: s.description,
                slots: s.type === 'pictures' ? s.slots : undefined,
                suppliesConfig: s.type === 'supplies' ? s.suppliesConfig : undefined,
            })),
        };
    }

    // Fallback: derive from INITIAL_TASK_SCREEN_CONFIG
    return {
        sections: INITIAL_TASK_SCREEN_CONFIG.sections.map((s) => ({
            type: s.type,
            enabled: s.enabled,
            defaultEnabled: true,
            title: s.title,
            description: s.description,
            slots: s.type === 'pictures' ? s.slots : undefined,
            suppliesConfig: s.type === 'supplies' ? s.suppliesConfig : undefined,
        })),
    };
}

function toAppTaskScreenConfig(values: FormValues): AppTaskScreenConfig {
    const sections = values.sections.map((s) => {
        const base = {
            enabled: s.enabled,
            defaultEnabled: s.defaultEnabled,
            title: s.title || undefined,
            description: s.description || undefined,
        };
        switch (s.type) {
            case 'pictures':
                return {
                    ...base,
                    type: 'pictures' as const,
                    slots: s.slots ?? [],
                };
            case 'supplies':
                return {
                    ...base,
                    type: 'supplies' as const,
                    suppliesConfig: (s.suppliesConfig ?? []).map((sc) => ({
                        ...sc,
                        equipmentTypeId: sc.equipmentTypeId as EquipmentTypeId,
                    })),
                };
            default:
                return { ...base, type: s.type };
        }
    });
    return {
        object: 'app-task-screen-config' as const,
        schema: 1 as const,
        id: 'task-screen-config' as const,
        sections: sections as AppTaskScreenConfig['sections'],
    };
}

export const DefaultTaskScreenPage: FC = () => {
    const appConfig = useAsyncAtomValue(appTaskScreenConfigState, undefined);

    const form = useForm<FormValues, unknown, FormValues>({
        resolver: zodResolver(FormSchema),
        defaultValues: toFormValues(appConfig),
    });

    const sections = useFieldArray({
        control: form.control,
        name: 'sections',
    });

    const onSubmit = useCallback(async (values: FormValues) => {
        try {
            const config = toAppTaskScreenConfig(values);
            await appDao.setTaskScreenConfig(config);
            toast.success('Default task screen config saved');
        } catch (err) {
            log.error('Failed to save default task screen config', { err });
            toast.error('Failed to save default task screen config');
        }
    }, []);

    return (
        <div className="p-6 max-w-3xl">
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Default Task Screen</CardTitle>
                            <CardDescription>
                                Configure the default sections that appear on task screens. Individual task types can
                                override these defaults. The "Default Enabled" toggle controls whether a section
                                defaults to ON or OFF for task types that haven't configured their own screen.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {sections.fields.map((field, index) => (
                                <DefaultSectionRow
                                    key={field.id}
                                    index={index}
                                    totalCount={sections.fields.length}
                                    form={form}
                                    onMoveUp={() => sections.move(index, index - 1)}
                                    onMoveDown={() => sections.move(index, index + 1)}
                                />
                            ))}
                        </CardContent>
                    </Card>

                    <Button type="submit" disabled={form.formState.isSubmitting}>
                        {form.formState.isSubmitting ? 'Saving...' : 'Save Default Config'}
                    </Button>
                </form>
            </Form>
        </div>
    );
};

type DefaultSectionRowProps = {
    index: number;
    totalCount: number;
    form: UseFormReturn<FormValues, unknown, FormValues>;
    onMoveUp: () => void;
    onMoveDown: () => void;
};

const DefaultSectionRow: FC<DefaultSectionRowProps> = ({ index, totalCount, form, onMoveUp, onMoveDown }) => {
    const sectionType = form.watch(`sections.${index}.type`);
    const isPictures = sectionType === 'pictures';

    return (
        <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={index === 0}
                        onClick={onMoveUp}
                    >
                        <ArrowUp className="h-3 w-3" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={index === totalCount - 1}
                        onClick={onMoveDown}
                    >
                        <ArrowDown className="h-3 w-3" />
                    </Button>
                </div>

                <span className="font-medium min-w-[100px]">{TASK_SECTION_LABELS[sectionType] ?? sectionType}</span>

                <FormField
                    control={form.control}
                    name={`sections.${index}.enabled`}
                    render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                            <FormLabel className="text-xs text-muted-foreground">Enabled</FormLabel>
                            <FormControl>
                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name={`sections.${index}.defaultEnabled`}
                    render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                            <FormLabel className="text-xs text-muted-foreground">Default Enabled</FormLabel>
                            <FormControl>
                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                        </FormItem>
                    )}
                />
            </div>

            <DefaultSectionFields index={index} form={form} isPictures={isPictures} />
        </div>
    );
};

type DefaultSectionFieldsProps = {
    index: number;
    form: UseFormReturn<FormValues, unknown, FormValues>;
    isPictures: boolean;
};

const DefaultSectionFields: FC<DefaultSectionFieldsProps> = ({ index, form, isPictures }) => {
    const slots = useFieldArray({
        control: form.control,
        name: `sections.${index}.slots`,
    });

    return (
        <div className="ml-12 space-y-3 border-l-2 pl-4">
            <FormField
                control={form.control}
                name={`sections.${index}.title`}
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className="text-xs">Title</FormLabel>
                        <FormControl>
                            <Input
                                className="bg-white"
                                placeholder="Section title"
                                {...field}
                                value={field.value || ''}
                            />
                        </FormControl>
                        <FormDescription>Display title for the section.</FormDescription>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name={`sections.${index}.description`}
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className="text-xs">Description</FormLabel>
                        <FormControl>
                            <Input
                                className="bg-white"
                                placeholder="Optional description..."
                                {...field}
                                value={field.value || ''}
                            />
                        </FormControl>
                        <FormDescription>Optional descriptive text shown in the section.</FormDescription>
                    </FormItem>
                )}
            />

            {isPictures && (
                <div className="space-y-2">
                    <FormLabel className="text-xs">Slots</FormLabel>
                    {slots.fields.map((slotField, slotIndex) => (
                        <div key={slotField.id} className="flex items-center gap-2">
                            <FormField
                                control={form.control}
                                name={`sections.${index}.slots.${slotIndex}.category`}
                                render={({ field }) => (
                                    <FormItem className="w-28">
                                        <Select value={field.value} onValueChange={field.onChange}>
                                            <FormControl>
                                                <SelectTrigger className="w-full min-w-0 bg-white text-sm">
                                                    <SelectValue />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="before">Before</SelectItem>
                                                <SelectItem value="after">After</SelectItem>
                                                <SelectItem value="other">Other</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name={`sections.${index}.slots.${slotIndex}.title`}
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormControl>
                                            <Input
                                                className="bg-white text-sm"
                                                placeholder="Display label"
                                                {...field}
                                                value={field.value || ''}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => slots.remove(slotIndex)}
                            >
                                <CircleX className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => slots.append({ category: 'other', title: '' })}
                    >
                        <PlusCircle className="h-4 w-4 mr-1" /> Add Slot
                    </Button>
                </div>
            )}
        </div>
    );
};
